import React, { useState } from 'react';
import { Button, Paper, Typography, Box, Grid, TextField, FormControl, Select, InputLabel, MenuItem } from '@mui/material';
import Question from './../../components/Assessment/question';
import { AssessmentQuestion, AssessmentQuestionType, MultipleChoiceQuestion, TextInputQuestion, FileUploadQuestion, Choice } from '../../utils/assessmentsQuestions';
import useFileHandlers from '../../hooks/useFileHandlers';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import IconButton from '@mui/material/IconButton';
import HttpHelper from '../../utils/HttpHelper';
import { useParams } from 'react-router-dom';
import SectionController from '../../components/Profile/SectionController';
import FileUpload from '../../components/common/FileUpload';
import CustomizedModal from '../../components/common/CustomizedModal';
import NotificationBar from "../../components/common/NotificationBar";
import { Navigate, useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import { isStringEmpty } from '../../utils/formValidation';

const AddQuestions: React.FC = () => {
    const { assessmentId } = useParams<{ assessmentId: string }>();
    const CREATE_QUESTION_ENDPOINT: string = `assessments/${assessmentId}/question/create`;
    const { handleFileUpload, handleDeleteFile, modalOpen, setModalOpen } = useFileHandlers();


    const [questions, setQuestions] = useState<AssessmentQuestion[]>([{
        id: null,
        type: AssessmentQuestionType.TextInput,
        description: '',
        answer: '',
        points: 0,
        file: null,
    }]);

    const [showNotification, setShowNotification] = useState(false);
    const [fileDeleted, setFileDeleted] = useState<boolean[]>(questions.map(() => false));
    const [descriptionFieldEmpty, setDescriptionFieldEmpty] = useState<boolean>(false);
    const [choiceFieldsEmpty, setChoiceFieldsEmpty] = useState<boolean>(false);

    const navigate = useNavigate();

    const [finalizedTypes, setFinalizedTypes] = useState<Set<number>>(new Set());

    const handleAddQuestion = () => {
        setQuestions(prevQuestions => [...prevQuestions, {
            id: null,
            type: AssessmentQuestionType.TextInput,
            description: '',
            answer: '',
            points: 0,
        }]);
    };

    const handleQuestionChange = (index: number, updatedQuestion: AssessmentQuestion) => {
        const updatedQuestions = questions.map((q, idx) => idx === index ? updatedQuestion : q);
        setQuestions(updatedQuestions);
    };

    const handleQuestionTypeChange = (index: number, type: AssessmentQuestionType) => {
        console.log(index, type);
        const updatedQuestions = questions.map((q, idx) => {
            if (idx === index) {
                switch (type) {
                    case AssessmentQuestionType.MultipleChoice:
                        return {
                            ...q,
                            type: type,
                            choices: q.type === AssessmentQuestionType.MultipleChoice || q.type === AssessmentQuestionType.SingleChoice ? q.choices : [{ choice_id: Date.now(), text: '' }],
                            answer: q.type === AssessmentQuestionType.MultipleChoice || q.type === AssessmentQuestionType.SingleChoice ? q.answer : [],
                            points: q.points,
                        } as MultipleChoiceQuestion;

                    case AssessmentQuestionType.SingleChoice:
                        return {
                            ...q,
                            type: type,
                            choices: q.type === AssessmentQuestionType.MultipleChoice || q.type === AssessmentQuestionType.SingleChoice ? q.choices : [{ choice_id: Date.now(), text: '' }],
                            answer: q.type === AssessmentQuestionType.MultipleChoice || q.type === AssessmentQuestionType.SingleChoice ? q.answer : [],
                            points: q.points,
                        };

                    case AssessmentQuestionType.TextInput:
                        return {
                            ...q,
                            type: type,
                            description: q.description,
                            answer: typeof q.answer === 'string' ? q.answer : '',
                            points: q.points,
                        } as TextInputQuestion;

                    case AssessmentQuestionType.FileUpload:
                        return {
                            ...q,
                            type: type,
                            description: q.description,
                            answer: typeof q.answer === 'string' ? q.answer : '',
                            points: q.points,
                        } as FileUploadQuestion;
                }
            }
            return q;
        });
        setQuestions(updatedQuestions);
    };


    const handleDeleteQuestion = (index: number) => {
        const question = questions[index];
        const newQuestions = [...questions].filter((_, i) => i !== index);
        setQuestions(newQuestions);
        setFinalizedTypes(prev => {
            const newFinalizedTypes = new Set(prev);
            newFinalizedTypes.delete(index);
            return newFinalizedTypes;
        });

        if (question.id) {

            try {
                HttpHelper.delete(`assessments/${assessmentId}/question/${question.id}/delete`);
            } catch (error) {
                console.error('Failed to delete the question', error);
            }
        }
    };


    const handleSaveUpdateQuestion = async (index: number) => {
        const question = questions[index];

        const descriptionFieldEmpty = isStringEmpty(question.description);
        setDescriptionFieldEmpty(descriptionFieldEmpty);

        let areChoicesEmpty = false;
        if (question.type === AssessmentQuestionType.MultipleChoice || question.type === AssessmentQuestionType.SingleChoice) {
            areChoicesEmpty = question.choices.some(choice => isStringEmpty(choice.text));
            setChoiceFieldsEmpty(areChoicesEmpty);
        }

        if (!descriptionFieldEmpty && !areChoicesEmpty) {
            console.log('descriptionFieldEmpty:', descriptionFieldEmpty);
            const questionData = {
                question: {
                    text: question.description,
                    type: question.type,
                    maxGrade: question.points
                },
                ...((question.type === AssessmentQuestionType.MultipleChoice || question.type === AssessmentQuestionType.SingleChoice) && {
                    possibleChoices: [...question.choices]
                        .sort((a, b) => a.choice_id - b.choice_id)
                        .map(choice => ({
                            option: choice.text,
                            isCorrect: choice.isCorrect ? 1 : 0,
                        }))
                })
            };

            if (question.id) {

                try {
                    await HttpHelper.put(`assessments/${assessmentId}/question/${question.id}/update`, questionData);
                    console.log('Question is updated successfully');
                } catch (error) {
                    console.error('Error updating question:', error);
                    return;
                }
            } else {
                try {
                    const response = await HttpHelper.post(CREATE_QUESTION_ENDPOINT, questionData);
                    if (response && response.questionId) {
                        questions[index].id = response.questionId;
                        setShowNotification(true);
                    }
                } catch (error) {
                    console.error('Error creating new question:', error);
                    return;
                }
            }

            setFinalizedTypes(prev => new Set(prev).add(index));
        }
    };

    const onFileSelect = (index: number, file: File) => {
        let updatedFileDeleted = [...fileDeleted];
        updatedFileDeleted[index] = false;
        setFileDeleted(updatedFileDeleted);
        handleFileUpload(index, file, assessmentId!, questions, setQuestions);
    };

    const onFileDelete = (index: number) => {
        let updatedFileDeleted = [...fileDeleted];
        updatedFileDeleted[index] = true;
        setFileDeleted(updatedFileDeleted);
        console.log('fileDeleted[index]' + fileDeleted[index]);
        handleDeleteFile(index, assessmentId!, questions, setQuestions);
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        navigate('/manage-assessments');
    };



    return (
        <Paper sx={{ padding: '2rem', margin: '2rem', width: '100%', marginLeft: 'auto', marginRight: 'auto' }}>
            <Typography variant="h5" fontWeight="bold">Add Questions</Typography>
            {questions.map((question, index) => (
                <Box key={question.id ?? index} sx={{ display: 'flex', flexDirection: 'column', marginBottom: '2rem', marginTop: '1rem' }}>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" sx={{ textAlign: 'left', fontWeight: 'bold' }}>Question {index + 1}</Typography>
                        <FormControl fullWidth variant="outlined" sx={{ my: 1 }}>
                            <InputLabel>Question Type</InputLabel>
                            <Select
                                value={question.type}
                                required
                                onChange={(e) => handleQuestionTypeChange(index, e.target.value as AssessmentQuestionType)}
                                label="Question Type"
                                disabled={finalizedTypes.has(index)}
                            >
                                <MenuItem value={AssessmentQuestionType.MultipleChoice}>Multiple Choice</MenuItem>
                                <MenuItem value={AssessmentQuestionType.SingleChoice}>Single Choice</MenuItem>
                                <MenuItem value={AssessmentQuestionType.TextInput}>Text Input</MenuItem>
                                <MenuItem value={AssessmentQuestionType.FileUpload}>File Upload</MenuItem>
                            </Select>
                        </FormControl>
                        <Question
                            key={question.id}
                            question={question}
                            onChange={(updatedQuestion) => handleQuestionChange(index, updatedQuestion)}
                            descriptionFieldEmpty={descriptionFieldEmpty}
                            choiceFieldsEmpty={choiceFieldsEmpty}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <FileUpload
                                fileUploadHandler={(file) => file && onFileSelect(index, file)}
                                buttonText="Upload File"
                                deleted={fileDeleted[index]}
                                canUpload={questions[index].id !== null}
                                onUploadConditionFail={() => setModalOpen(true)}
                            />
                            <Button
                                onClick={() => onFileDelete(index)}
                                sx={{
                                    color: 'red',
                                    textTransform: 'uppercase',
                                    whiteSpace: 'nowrap',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    ml: 2,
                                }}
                            >
                                Delete File
                            </Button>
                        </Box>

                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
                        <SectionController
                            saveButtonHandler={() => handleSaveUpdateQuestion(index)}
                            deleteButtonHandler={() => handleDeleteQuestion(index)}
                        />

                    </Box>
                </Box>

            ))}

            <CustomizedModal
                open={modalOpen}
                clickHandler={() => setModalOpen(false)}
                title="Attention"
                description="Please save the question before uploading any files."
                buttonText="Close"
            />
            <NotificationBar
                open={showNotification}
                setOpen={setShowNotification}
                title='Successs!'
                message='Question is Created Successfully!'
                hideDuraction={2000}
            />

            <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
                <IconButton onClick={handleAddQuestion} sx={{ color: "var(--ttg-black)" }}>
                    <AddCircleIcon />
                </IconButton>
            </Box>
            <form onSubmit={handleSubmit}>

                <Button
                    type="submit"
                    variant="contained"

                    sx={{
                        backgroundColor: 'var(--ttg-green)',
                        '&:hover': {
                            backgroundColor: 'var(--button-hover-green)',
                        },
                        marginTop: 3,
                        marginBottom: 3,
                    }}
                >
                    Create An Assessment
                </Button>
            </form>
        </Paper>
    );

};

export default AddQuestions;
